import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Printer, ExternalLink, Download } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { getLang } from "@/lib/i18n";
import { credentialNote } from "@/lib/hooks";

export const verifyUrl = (c) => `${(c.verify_base_url || `${window.location.origin}/verify`).replace(/\/$/, "")}/${c.credential_id}`;

export const CertificateCard = ({ c }) => {
  const primary = c.colors?.primary || "#1F9FD0";
  const accent = c.colors?.accent || "#4B3F9E";
  return (
    <div data-testid="certificate-card" className="relative overflow-hidden border border-neutral-300 bg-[#fbfaf6] p-8 text-neutral-900 sm:p-14" style={{ borderTop: `6px solid ${primary}` }}>
      <div className="flex items-start justify-between gap-6">
        {c.logo_url ? <img src={assetUrl(c.logo_url)} alt={`${c.brand_name} logo`} className="h-12 w-auto max-w-[220px] object-contain" /> : <span className="font-heading text-xl font-black">{c.brand_name}</span>}
        <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">{c.edition_label}</span>
      </div>
      <div className="mt-14 font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">This certifies that</div>
      <div data-testid="certificate-learner-name" className="mt-3 font-serif text-4xl italic sm:text-5xl">{c.learner_name}</div>
      <div className="mt-6 text-neutral-600">{c.tier === "executive" ? "has passed the Executive Briefing leader quiz at the 80% standard for" : `has met the 80% standard on all eight certified modules${c.capstone_score != null ? " and passed the Line 3 capstone simulation" : ""} for`}</div>
      <div className="mt-3 font-heading text-2xl font-black uppercase tracking-tight sm:text-3xl">{c.course_title}</div>
      <div data-testid="certificate-tier" className="mt-2 inline-block px-3 py-1 font-heading text-sm font-bold uppercase tracking-widest text-white" style={{ background: accent }}>{c.tier === "executive" ? c.tier_label : `${c.tier_label} level`}</div>
      <div className="mt-12 grid gap-6 border-t border-neutral-300 pt-6 text-sm sm:grid-cols-4">
        <div><div className="text-xs text-neutral-500">Issued by</div><div className="font-semibold">{c.issuer}</div></div>
        <div><div className="text-xs text-neutral-500">Issue date</div><div className="font-semibold">{new Date(c.issued_at).toLocaleDateString(getLang())}</div></div>
        <div><div className="text-xs text-neutral-500">{c.tier === "executive" ? "Score" : "Average score"}</div><div className="font-semibold">{c.average_score}%{c.capstone_score != null && ` · capstone ${c.capstone_score}%`}</div></div>
        <div><div className="text-xs text-neutral-500">Credential ID</div><div data-testid="certificate-id" className="font-mono font-semibold">{c.credential_id}</div></div>
      </div>
      <p className="mt-8 text-[11px] text-neutral-500">{credentialNote()} Verify at {verifyUrl(c)}</p>
    </div>
  );
};

const PILOT_BRIEF = "# Pilot brief\n\n- Target line:\n- Data sources and owners:\n- Architecture (connector / RAG / graph / tool):\n- Safety boundary:\n- Adoption workflow and owner:\n- Success measures and baseline:\n";

const ConversionGate = () => {
  const { edition } = useBrand();
  if (!edition) return null;
  const track = () => api.post("/events", { type: "cta_click", edition_slug: edition.slug, meta: { label: edition.cta_label } });
  const brief = () => {
    const url = URL.createObjectURL(new Blob([PILOT_BRIEF], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "pilot-brief.md";
    a.click();
  };
  return (
    <div data-testid="conversion-gate" className="no-print mt-10 bg-trust p-8 text-white sm:p-10">
      <p className="max-w-2xl font-serif text-3xl italic leading-snug">Your model is only as useful as the plant data it can see. Ready to map your live data blind spots and move from prompt to insight?</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a href={edition.cta_url} target="_blank" rel="noreferrer" onClick={track} data-testid="cta-primary-button" className="inline-flex items-center gap-2 rounded-sm bg-brand-action px-5 py-2.5 font-semibold text-brand-action-fg">{edition.cta_label} <ExternalLink className="h-4 w-4" /></a>
        <button onClick={brief} data-testid="cta-secondary-button" className="inline-flex items-center gap-2 rounded-sm border border-white/30 px-5 py-2.5 font-semibold hover:bg-white/10"><Download className="h-4 w-4" /> {edition.secondary_cta_label}</button>
      </div>
    </div>
  );
};

export function CertificateView() {
  const { credentialId } = useParams();
  const { data: c } = useQuery({ queryKey: ["cert", credentialId], queryFn: async () => (await api.get(`/certificates/verify/${credentialId}`)).data });
  if (!c) return <Loader />;
  const copy = () => { navigator.clipboard.writeText(verifyUrl(c)); toast.success("Verification link copied"); };
  return (
    <PageShell>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link to="/certificates" className="text-sm text-muted-foreground hover:text-foreground">← All certificates</Link>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="certificate-copy-link-button" className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"><Copy className="h-4 w-4" /> Copy verification link</button>
          <button onClick={() => window.print()} data-testid="certificate-print-button" className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"><Printer className="h-4 w-4" /> Print / PDF</button>
        </div>
      </div>
      <CertificateCard c={c} />
      <ConversionGate />
    </PageShell>
  );
}

export default function Certificates() {
  const { data: certs } = useQuery({ queryKey: ["certs"], queryFn: async () => (await api.get("/certificates")).data });
  if (!certs) return <Loader />;
  return (
    <PageShell>
      <div className="overline-label">Credentials</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">Certificates</h1>
      {certs.length === 0 && <p data-testid="certificates-empty" className="mt-6 text-muted-foreground">No certificates yet. Pass all eight certified module quizzes at Foundation, or the Executive decision check, to earn your first.</p>}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {certs.map((c) => (
          <Link key={c.credential_id} to={`/certificates/${c.credential_id}`} data-testid={`certificate-item-${c.tier}`} className="border border-border bg-card p-6 transition-transform hover:-translate-y-0.5">
            <div className="overline-label">{c.brand_name}</div>
            <div className="mt-2 font-heading text-xl font-bold">{c.tier_label}</div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{c.credential_id}</div>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}
